import { mountRangeControl, useCustomSlider } from './rangeAdapter';
import type { ScaleKind } from '../controls/rangeMath';
import type { TBindingLike, TFolderLike } from './types';

// `bindSlider` wraps `folder.addBinding` and mounts the dependency-free
// RangeControl into the binding's value cell, returning a `TBindingLike` shim
// so every `bindSlider(...).on('change', …)` caller — and `bindControl` above
// it — routes through one primitive, store dispatch unchanged.
//
// Tick density: coarse sliders get one mark per discrete `step` (legible only
// between 2 and MAX_TICK_INTERVALS stops); log/symlog instead emit decade ticks
// regardless of step. min/max/step ride along in the same opts object.
const MAX_TICK_INTERVALS = 24;

/** `folder.addBinding` with step-tick decoration (or the custom RangeControl
 *  behind the flag). Drop-in: returns a `TBindingLike`, so `.on('change', …)`
 *  chaining is unaffected. The `scale`/`linthresh`/`origin` opts are consumed
 *  by the custom path only. */
export function bindSlider<T extends object, K extends keyof T>(
    folder: TFolderLike,
    target: T,
    key: K,
    opts: Record<string, unknown>,
): TBindingLike {
    const { scale, linthresh, origin, minorTicks, ...nativeOpts } = opts as {
        scale?: ScaleKind;
        linthresh?: number;
        origin?: number;
        minorTicks?: boolean;
    } & Record<string, unknown>;
    const binding = folder.addBinding(target, key, nativeOpts);

    const min = nativeOpts['min'];
    const max = nativeOpts['max'];
    const step = nativeOpts['step'];
    if (typeof min !== 'number' || typeof max !== 'number') return binding;

    const stepNum = typeof step === 'number' && step > 0 ? step : undefined;
    const intervals = stepNum ? Math.round((max - min) / stepNum) : 0;
    // Linear step-ticks (one mark per discrete step) — only when the count is
    // legible (2..24). Log/symlog instead want decade ticks: the custom slider's
    // tickPositions emits powers of ten (count-capped), independent of step.
    const stepTicksOn = intervals >= 2 && intervals <= MAX_TICK_INTERVALS;
    const customTicks = scale === 'log' || scale === 'symlog' ? true : stepTicksOn;

    if (useCustomSlider) {
        const bag = target as Record<string, unknown>;
        const k = key as string;
        const fmt = nativeOpts['format'];
        const listeners: Array<(e: { value: unknown }) => void> = [];
        const commit = (v: number | readonly [number, number]): void => {
            bag[k] = v as number; // single mode only here, so `v` is always a number
            for (const l of listeners) l({ value: v });
        };
        const ctrl = mountRangeControl(binding, {
            min, max,
            ...(stepNum !== undefined ? { step: stepNum } : {}),
            value: Number(bag[k]),
            ...(typeof fmt === 'function' ? { format: fmt as (v: number) => string } : {}),
            ...(customTicks ? { ticks: true } : {}),
            ...(scale !== undefined ? { scale } : {}),
            ...(typeof linthresh === 'number' ? { linthresh } : {}),
            ...(typeof origin === 'number' ? { origin } : {}),
            ...(typeof minorTicks === 'boolean' ? { minorTicks } : {}),
            onInput: commit,
            onChange: commit,
        });
        if (ctrl) {
            const el = binding.element;
            const shim: TBindingLike = {
                ...(el ? { element: el } : {}),
                get disabled(): boolean { return binding.disabled ?? false; },
                set disabled(v: boolean) {
                    binding.disabled = v;
                    ctrl.el.style.opacity = v ? '0.5' : '';
                    ctrl.el.style.pointerEvents = v ? 'none' : '';
                },
                on(ev, cb) { if (ev === 'change') listeners.push(cb); return this; },
            };
            return shim;
        }
    }

    return binding;
}
